import { Tooltip, message } from "antd"
import copyToClipboard from "copy-to-clipboard"
import type { ReactElement } from "react"

import { Button } from "@/components/design-system/Button/Button"
import { TextBodyMono } from "@/components/design-system/Text/TextBodyMono"
import { ErrorPage } from "@/components/global/Errors/ErrorPage"
import { viewManager } from "@/util/viewManager"

import "./AppErrorPage.less"

interface AppErrorPageProps {
  onReload?: () => void
}

export const appErrorPageActions = {
  reload: () => window.location.reload(),
}

async function copyTransactionId(transactionId: string): Promise<void> {
  try {
    const ok = await copyToClipboard(transactionId)
    if (!ok) {
      void message.error("Failed to copy transaction ID")
      return
    }

    void message.success("Copied transaction ID to clipboard")
  } catch {
    void message.error("Failed to copy transaction ID")
  }
}

/**
 * Renders the generic fallback for unhandled app render crashes.
 * Real 503 unavailable responses use the maintenance page instead.
 */
export function AppErrorPage({
  onReload = appErrorPageActions.reload,
}: AppErrorPageProps): ReactElement {
  const transactionId = viewManager.get("transactionId")

  return (
    <ErrorPage
      title="Something went wrong"
      subtitle="We ran into an unexpected error loading this page. Our team has been notified — please try again."
    >
      <Button type="primary" className="thick" onClick={onReload}>
        Reload
      </Button>
      {transactionId ? (
        <Tooltip title="Click to copy">
          <button
            type="button"
            className="AppErrorPage__reference"
            onClick={() => void copyTransactionId(transactionId)}
          >
            Transaction ID: <TextBodyMono type="secondary">{transactionId}</TextBodyMono>
          </button>
        </Tooltip>
      ) : null}
    </ErrorPage>
  )
}
